/**
 * ProfileDialog — edit user name / account type / subcategory, manage presenter
 * upgrade, and quick-link to API Keys page. Parent owns the API call.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Sparkles, Presentation, Key, CreditCard, Receipt, XCircle, TrendingUp, Bell, Mail, Compass } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import { useRestartOnboardingTour } from "@/components/OnboardingTour";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProfileDialog({
  t, open, onOpenChange,
  user, profileForm, setProfileForm,
  hasPresenterAccess, upgrading,
  onUpgradeToPresenter, onSave,
}) {
  const navigate = useNavigate();
  const { token } = useAuth();
  const restartTour = useRestartOnboardingTour();
  const [subscription, setSubscription] = useState(null);
  const [subLoading, setSubLoading] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [communities, setCommunities] = useState([]);
  const [notifSaving, setNotifSaving] = useState(null); // community_id being saved

  useEffect(() => {
    if (!open || !token) return;
    setSubLoading(true);
    fetch(`${API_URL}/api/payments/subscription`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => setSubscription(d))
      .catch(() => setSubscription(null))
      .finally(() => setSubLoading(false));

    // Load communities for notification-preferences panel
    fetch(`${API_URL}/api/communities`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => setCommunities(Array.isArray(list) ? list : []))
      .catch(() => setCommunities([]));
  }, [open, token]);

  const toggleNotificationPref = async (communityId, key, next) => {
    setNotifSaving(communityId);
    // Optimistic update
    setCommunities((prev) => prev.map((c) =>
      c.community_id === communityId
        ? { ...c, notification_preferences: { ...(c.notification_preferences || {}), [key]: next } }
        : c
    ));
    try {
      const r = await fetch(`${API_URL}/api/communities/${communityId}/notification-preferences`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [key]: next }),
      });
      if (!r.ok) throw new Error("save_failed");
      toast.success(next ? "Notification enabled" : "Notification disabled");
    } catch {
      setCommunities((prev) => prev.map((c) =>
        c.community_id === communityId
          ? { ...c, notification_preferences: { ...(c.notification_preferences || {}), [key]: !next } }
          : c
      ));
      toast.error("Failed to save preference");
    } finally {
      setNotifSaving(null);
    }
  };

  const handleCancel = async () => {
    setCanceling(true);
    try {
      const r = await fetch(`${API_URL}/api/payments/subscription/cancel`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (r.ok) {
        toast.success(data.message || "Subscription will end at period end");
        setSubscription((prev) => ({ ...(prev || {}), status: "canceling" }));
      } else {
        toast.error(data.detail || "Cancel failed");
      }
    } catch {
      toast.error("Cancel failed");
    } finally {
      setCanceling(false);
      setCancelDialogOpen(false);
    }
  };

  const tier = subscription?.tier || "free";
  const isPaid = tier && tier !== "free";
  const periodEnd = subscription?.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString()
    : null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Profile Settings</DialogTitle>
          <DialogDescription>Update your profile information and account type</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="flex justify-center mb-4">
            <Avatar className="w-20 h-20">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="text-2xl">{user?.name?.charAt(0) || "U"}</AvatarFallback>
            </Avatar>
          </div>
          <div className="space-y-2">
            <Label>{t("name") || "Name"}</Label>
            <Input
              value={profileForm.name}
              onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
              placeholder={t("your_name") || "Your name"}
            />
          </div>
          <div className="space-y-2">
            <Label>{t("email") || "Email"}</Label>
            <Input value={profileForm.email} disabled className="bg-slate-100 dark:bg-slate-800" />
            <p className="text-xs text-muted-foreground">{t("email_cannot_change") || "Email cannot be changed"}</p>
          </div>
          <div className="space-y-2">
            <Label>{t("account_type") || "Account Type"}</Label>
            <Select
              value={profileForm.user_type}
              onValueChange={(v) => setProfileForm({ ...profileForm, user_type: v, user_subcategory: "" })}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("select_type") || "Select type..."} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="church">{t("church") || "Church"}</SelectItem>
                <SelectItem value="educator">{t("educator") || "Educator"}</SelectItem>
                <SelectItem value="presenter">{t("presenter") || "Presenter"}</SelectItem>
                <SelectItem value="user">{t("user") || "User"}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {profileForm.user_type && profileForm.user_type !== "presenter" && (
            <div className="space-y-2">
              <Label>{t("subcategory") || "Subcategory"}</Label>
              <Select
                value={profileForm.user_subcategory}
                onValueChange={(v) => setProfileForm({ ...profileForm, user_subcategory: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("select_subcategory") || "Select subcategory..."} />
                </SelectTrigger>
                <SelectContent>
                  {profileForm.user_type === "church" && (
                    <>
                      <SelectItem value="admin">{t("admin") || "Admin"}</SelectItem>
                      <SelectItem value="pastor">{t("pastor") || "Pastor"}</SelectItem>
                    </>
                  )}
                  {profileForm.user_type === "educator" && (
                    <SelectItem value="teacher">{t("teacher") || "Teacher"}</SelectItem>
                  )}
                  {profileForm.user_type === "user" && (
                    <>
                      <SelectItem value="student">{t("student") || "Student"}</SelectItem>
                      <SelectItem value="pastor">{t("pastor") || "Pastor"}</SelectItem>
                      <SelectItem value="regular">{t("regular") || "Regular"}</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          {profileForm.user_type === "user" && !hasPresenterAccess && (
            <Card className="border-2 border-dashed border-emerald-500/50 bg-emerald-500/5">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-emerald-500/10">
                    <Presentation className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-sm">{t("upgrade_to_presenter") || "Upgrade to Presenter"}</h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t("upgrade_description") || "Get access to create professional slideshows, use AI features, and present to audiences."}
                    </p>
                    <Button
                      size="sm"
                      className="mt-3 bg-emerald-600 hover:bg-emerald-700"
                      onClick={onUpgradeToPresenter}
                      disabled={upgrading}
                      data-testid="upgrade-presenter-btn"
                    >
                      {upgrading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t("upgrading") || "Upgrading..."}
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          {t("upgrade_now") || "Upgrade Now"}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {hasPresenterAccess && profileForm.user_type === "user" && (
            <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
              <Presentation className="w-5 h-5 text-emerald-500" />
              <div>
                <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                  {t("presenter_access_active") || "Presenter Access Active"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("presenter_access_desc") || "You can create and present professional slideshows"}
                </p>
              </div>
            </div>
          )}

          {/* Manage Subscription */}
          <div className="border-t pt-4 mt-2" data-testid="manage-subscription-section">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-slate-500" />
                <h4 className="text-sm font-semibold">{t("manage_subscription") || "Manage Subscription"}</h4>
              </div>
              {subLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
            </div>

            <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-500">{t("current_plan") || "Current plan"}</span>
                <span className="text-sm font-medium capitalize" data-testid="current-tier-label">
                  {tier}
                  {subscription?.status === "canceling" && (
                    <span className="ml-2 text-[10px] uppercase text-amber-600">
                      {t("canceling") || "Canceling"}
                    </span>
                  )}
                </span>
              </div>
              {periodEnd && isPaid && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">
                    {subscription?.status === "canceling"
                      ? (t("access_until") || "Access until")
                      : (t("renews_on") || "Renews on")}
                  </span>
                  <span className="text-xs font-mono">{periodEnd}</span>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { onOpenChange(false); navigate("/pricing"); }}
                  className="gap-1.5 flex-1 min-w-[110px]"
                  data-testid="change-plan-btn"
                >
                  <TrendingUp className="w-3.5 h-3.5" />
                  {isPaid ? (t("change_plan") || "Change plan") : (t("upgrade") || "Upgrade")}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { onOpenChange(false); navigate("/invoices"); }}
                  className="gap-1.5 flex-1 min-w-[110px]"
                  data-testid="view-invoices-btn"
                >
                  <Receipt className="w-3.5 h-3.5" />
                  {t("invoices") || "Invoices"}
                </Button>
              </div>

              {isPaid && subscription?.status !== "canceling" && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setCancelDialogOpen(true)}
                  disabled={canceling}
                  className="w-full gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30 mt-1"
                  data-testid="cancel-subscription-btn"
                >
                  {canceling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
                  {t("cancel_subscription") || "Cancel subscription"}
                </Button>
              )}
              {subscription?.status === "canceling" && (
                <p className="text-[11px] text-amber-600 dark:text-amber-400 text-center pt-1">
                  {t("cancel_scheduled") || "Cancellation scheduled — you keep access until the period ends."}
                </p>
              )}
            </div>
          </div>

          {/* Notification preferences */}
          <div className="border-t pt-4" data-testid="notification-preferences-section">
            <div className="flex items-center gap-2 mb-3">
              <Bell className="w-4 h-4 text-slate-500" />
              <h4 className="text-sm font-semibold">
                {t("notification_preferences") || "Notification preferences"}
              </h4>
            </div>
            {communities.length === 0 ? (
              <p className="text-xs text-slate-500 italic">
                {t("no_communities_notif") || "Join a community to manage per-community reminders."}
              </p>
            ) : (
              <div className="rounded-lg border border-slate-200 dark:border-slate-800 divide-y divide-slate-200 dark:divide-slate-800">
                {communities.map((c) => {
                  const emailOn = c.notification_preferences?.email_reminders !== false;
                  const digestOn = c.notification_preferences?.weekly_digest === true;
                  return (
                    <div
                      key={c.community_id}
                      className="p-3 space-y-2"
                      data-testid={`community-notif-row-${c.community_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                          <Mail className="w-4 h-4 text-slate-500" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{c.name || "Untitled community"}</p>
                        </div>
                        {notifSaving === c.community_id && (
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400 shrink-0" />
                        )}
                      </div>
                      <div className="flex items-center justify-between pl-11">
                        <div className="min-w-0">
                          <p className="text-xs text-slate-700 dark:text-slate-300">
                            {t("service_reminders_24h") || "Service reminders · 24h before"}
                          </p>
                        </div>
                        <Switch
                          checked={emailOn}
                          onCheckedChange={(v) => toggleNotificationPref(c.community_id, "email_reminders", v)}
                          data-testid={`notif-switch-${c.community_id}`}
                        />
                      </div>
                      <div className="flex items-center justify-between pl-11">
                        <div className="min-w-0">
                          <p className="text-xs text-slate-700 dark:text-slate-300">
                            {t("weekly_digest") || "Weekly digest · Sundays 8pm UTC"}
                          </p>
                        </div>
                        <Switch
                          checked={digestOn}
                          onCheckedChange={(v) => toggleNotificationPref(c.community_id, "weekly_digest", v)}
                          data-testid={`digest-switch-${c.community_id}`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => { onOpenChange(false); restartTour(); }}
              className="mt-3 w-full gap-1.5 text-slate-600 dark:text-slate-400"
              data-testid="restart-tour-btn"
            >
              <Compass className="w-3.5 h-3.5" />
              {t("restart_tour") || "Take the tour again"}
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => { onOpenChange(false); navigate("/integrations"); }}
            data-testid="open-api-keys-btn"
            className="gap-1"
          >
            <Key className="w-3.5 h-3.5" />
            {t("api_keys") || "API Keys"}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave}>Save Changes</Button>
        </DialogFooter>
      </DialogContent>

      {/* Cancel Subscription confirmation */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent data-testid="cancel-subscription-alert">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("cancel_subscription_title") || "Cancel your subscription?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("cancel_subscription_body") || "You will keep your current tier until the end of the billing period, then downgrade to Free. You can re-subscribe any time."}
              {periodEnd && (
                <span className="block mt-2 text-slate-700 dark:text-slate-300">
                  <b>{t("access_until") || "Access until"}:</b> {periodEnd}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-subscription-abort">
              {t("keep_subscription") || "Keep subscription"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              disabled={canceling}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="cancel-subscription-confirm"
            >
              {canceling ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />{t("canceling") || "Canceling..."}</>
              ) : (t("cancel_subscription") || "Cancel subscription")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
